// @ts-check
/** @typedef {import('../types/api').InstagramPost} InstagramPost */
/** @typedef {import('../types/api').ScheduledPost} ScheduledPost */
/** @typedef {import('../types/api').PostInsights} PostInsights */
/** @typedef {import('../types/api').PostRecommendation} PostRecommendation */

import {
  BarChart3,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  Copy,
  Edit2,
  Eye,
  Hash,
  Image,
  Loader,
  MapPin,
  Play,
  Plus,
  RefreshCw,
  Search,
  ShoppingBag,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import FileUpload from '../components/FileUpload';
import LoadingScreen from '../components/LoadingScreen';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal, { ModalDrawer } from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import postMarketingService from '../services/postMarketingService';
import { looksLikeVideoUrl } from '../utils/mediaThumbnail';

// ─── Constants ─────────────────────────────────────────────────────────────────

const TARGET_MARKET_OPTIONS = [
  'India',
  'United States',
  'United Kingdom',
  'UAE',
  'France',
  'Italy',
  'Japan',
  'Germany',
  'Australia',
  'Global',
];

const POST_TYPE_OPTIONS = ['Image', 'Carousel', 'Reel', 'Story'];

const POST_STATUS = {
  draft: { label: 'Draft', bg: 'bg-muted', text: 'text-foreground/80' },
  scheduled: { label: 'Scheduled', bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  published: {
    label: 'Published',
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-700 dark:text-green-300',
  },
  failed: { label: 'Failed', bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' },
};

const StatusBadge = ({ value }) => {
  const info = POST_STATUS[value] || { label: value, bg: 'bg-muted', text: 'text-foreground/80' };
  return <span className={`px-2 py-1 text-xs rounded-full font-medium ${info.bg} ${info.text}`}>{info.label}</span>;
};

// ─── Searchable Dropdown Component ─────────────────────────────────────────────

const SearchableDropdown = ({ value, onChange, options, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      {label && <label className="block text-sm font-medium text-foreground/80 mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent bg-card text-left"
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground/70'}>{value || placeholder}</span>
        <ChevronDown size={16} className="text-muted-foreground/70" />
      </button>
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-border/60">
            <div className="flex items-center gap-2 border border-border rounded-lg px-2 py-1.5">
              <Search size={14} className="text-muted-foreground/70" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full text-sm outline-none"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-44">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground/70">No results</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 ${opt === value ? 'bg-muted/50 font-medium' : ''}`}
                >
                  {opt}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// A KaayPost's post_type is set by the Instagram sync (routes/admin/posts_marketing.py) to
// 'video' or 'reel' for anything with a playable media_url — everything else is a still image.
const isVideoPost = (post) => post.post_type === 'video' || post.post_type === 'reel';

// ─── Main Component ───────────────────────────────────────────────────────────

const PostManagementPage = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('synced');
  const [loading, setLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [analysingPostId, setAnalysingPostId] = useState(null);

  // Insights state
  /** @type {[PostInsights | null, Function]} */
  const [insights, setInsights] = useState(null);

  // Upload & Optimise state
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState('');
  const [captionDraft, setCaptionDraft] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [postType, setPostType] = useState('');
  const [locationTag, setLocationTag] = useState('');
  /** @type {[PostRecommendation | null, Function]} */
  const [recommendation, setRecommendation] = useState(null);
  const [recommendLoading, setRecommendLoading] = useState(false);

  // Schedule modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDirty, setScheduleDirty] = useState(false);
  const [scheduleForm, setScheduleFormRaw] = useState({
    caption: '',
    scheduled_at: '',
    location_tag: '',
    hashtags: '',
    status: 'scheduled',
    media_url: '',
  });
  const setScheduleForm = useCallback((updater) => {
    setScheduleDirty(true);
    setScheduleFormRaw(updater);
  }, []);
  const closeScheduleModal = () => {
    if (scheduleDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setShowScheduleModal(false);
  };

  // Create new scheduled post modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const [createForm, setCreateFormRaw] = useState({
    caption: '',
    scheduled_at: '',
    location_tag: '',
    hashtags: '',
    status: 'draft',
    media_url: '',
  });
  const setCreateForm = useCallback((updater) => {
    setCreateDirty(true);
    setCreateFormRaw(updater);
  }, []);
  const closeCreateModal = () => {
    if (createDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setShowCreateModal(false);
  };

  // Quick-schedule a draft (datetime picker modal)
  const [quickScheduleId, setQuickScheduleId] = useState(null);
  const [quickScheduleDatetime, setQuickScheduleDatetimeRaw] = useState('');
  const [quickScheduleDirty, setQuickScheduleDirty] = useState(false);
  const setQuickScheduleDatetime = useCallback((value) => {
    setQuickScheduleDirty(true);
    setQuickScheduleDatetimeRaw(value);
  }, []);
  const closeQuickScheduleModal = () => {
    if (quickScheduleDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setQuickScheduleId(null);
    setQuickScheduleDatetimeRaw('');
    setQuickScheduleDirty(false);
  };

  // Edit scheduled modal state
  const [editingScheduled, setEditingScheduledRaw] = useState(null);
  const [editingScheduledDirty, setEditingScheduledDirty] = useState(false);
  const setEditingScheduled = useCallback((updater) => {
    setEditingScheduledDirty(true);
    setEditingScheduledRaw(updater);
  }, []);
  const closeEditScheduledModal = () => {
    if (editingScheduledDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setEditingScheduledRaw(null);
    setEditingScheduledDirty(false);
  };

  // Copy state
  const [copiedField, setCopiedField] = useState(null);

  // Link Product state
  /** @type {[any[], Function]} */
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [linkedProductId, setLinkedProductId] = useState(null);
  const [savingLink, setSavingLink] = useState(false);

  // ─── Data Fetching (infinite scroll) ───────────────────────────────────────

  const fetchPostsFn = useCallback((page, limit) => postMarketingService.getPosts({ page, limit }), []);
  const {
    items: posts,
    loading: postsLoading,
    reset: resetPosts,
    sentinelRef: postsSentinelRef,
  } = useInfiniteScroll(fetchPostsFn, { limit: 50, deps: [] });

  const fetchScheduledFn = useCallback((page, limit) => postMarketingService.getScheduled({ page, limit }), []);
  const {
    items: scheduledPosts,
    loading: scheduledLoading,
    reset: resetScheduled,
    sentinelRef: scheduledSentinelRef,
  } = useInfiniteScroll(fetchScheduledFn, { limit: 50, deps: [] });

  // Fetch insights when synced tab is active
  useEffect(() => {
    if (activeTab === 'synced') {
      postMarketingService
        .getInsights()
        .then((data) => setInsights(data))
        .catch(() => {});
    }
  }, [activeTab]);

  // ─── Synced Posts Handlers ────────────────────────────────────────────────

  const handleSyncPosts = async () => {
    try {
      setLoading(true);
      await postMarketingService.syncPosts();
      showToast('Posts synced successfully', 'success');
      resetPosts();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPost = async (post) => {
    setSelectedPost(post);
    setSelectedAnalysis(null);
    setLinkedProductId(post.product_id || null);
    if (post.has_analysis) {
      try {
        const analysis = await postMarketingService.getAnalysis(post.id);
        setSelectedAnalysis(analysis);
      } catch (error) {
        showToast(error.message, 'error');
      }
    }
    // Load products list once
    if (!productsLoaded) {
      try {
        const data = /** @type {any} */ (await postMarketingService.getProducts());
        const products = Array.isArray(data) ? data : data?.products || data?.items || [];
        setAllProducts(products);
        setProductsLoaded(true);
      } catch (error) {
        console.error('Failed to load products for linking', error);
      }
    }
  };

  const handleAnalysePost = async (postId) => {
    try {
      setAnalysingPostId(postId);
      const analysis = await postMarketingService.analysePost(postId);
      setSelectedAnalysis(analysis);
      showToast('Post analysed successfully', 'success');
      resetPosts();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setAnalysingPostId(null);
    }
  };

  // ─── Link Product Handler ──────────────────────────────────────────────────

  const handleLinkProduct = async (productId) => {
    if (!selectedPost) return;
    try {
      setSavingLink(true);
      await postMarketingService.linkProduct(selectedPost.id, productId);
      setLinkedProductId(productId);
      showToast(productId ? 'Product linked successfully' : 'Product unlinked', 'success');
      setSelectedPost((prev) => (prev ? { ...prev, product_id: productId } : prev));
    } catch (error) {
      showToast(error.message || 'Failed to link product', 'error');
    } finally {
      setSavingLink(false);
    }
  };

  // ─── Upload & Optimise Handlers ───────────────────────────────────────────

  const handleGetRecommendation = async () => {
    if (!captionDraft.trim() && !uploadedMediaUrl) {
      showToast('Please provide a caption or upload media first', 'error');
      return;
    }
    try {
      setRecommendLoading(true);
      const data = await postMarketingService.getRecommendation({
        caption: captionDraft,
        media_url: uploadedMediaUrl,
        target_market: targetMarket,
        post_type: postType,
        location_tag: locationTag,
      });
      setRecommendation(data);
      showToast('Recommendation generated', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setRecommendLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleOpenScheduleFromRecommendation = () => {
    setScheduleFormRaw({
      caption: recommendation?.recommended_caption || captionDraft,
      scheduled_at: '',
      location_tag: recommendation?.location_tag || locationTag || '',
      hashtags: Array.isArray(recommendation?.hashtags)
        ? recommendation.hashtags.join(', ')
        : recommendation?.hashtags || '',
      status: 'scheduled',
      media_url: uploadedMediaUrl,
    });
    setScheduleDirty(false);
    setShowScheduleModal(true);
  };

  // ─── Scheduled Posts Handlers ─────────────────────────────────────────────

  const handleCreateScheduled = async (formData, switchTab = false) => {
    try {
      setLoading(true);
      await postMarketingService.createScheduled({
        ...formData,
        hashtags: formData.hashtags
          .split(',')
          .map((h) => h.trim())
          .filter(Boolean),
      });
      showToast('Scheduled post created', 'success');
      setShowScheduleModal(false);
      setShowCreateModal(false);
      setScheduleFormRaw({
        caption: '',
        scheduled_at: '',
        location_tag: '',
        hashtags: '',
        status: 'scheduled',
        media_url: '',
      });
      setScheduleDirty(false);
      setCreateFormRaw({
        caption: '',
        scheduled_at: '',
        location_tag: '',
        hashtags: '',
        status: 'draft',
        media_url: '',
      });
      setCreateDirty(false);
      if (switchTab) {
        setActiveTab('scheduled');
      }
      resetScheduled();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateScheduled = async () => {
    if (!editingScheduled) return;
    try {
      setLoading(true);
      await postMarketingService.updateScheduled(editingScheduled.id, {
        ...editingScheduled,
        hashtags:
          typeof editingScheduled.hashtags === 'string'
            ? editingScheduled.hashtags
                .split(',')
                .map((h) => h.trim())
                .filter(Boolean)
            : editingScheduled.hashtags,
      });
      showToast('Scheduled post updated', 'success');
      setEditingScheduledRaw(null);
      setEditingScheduledDirty(false);
      resetScheduled();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScheduled = async (id) => {
    if (!window.confirm('Delete this scheduled post?')) return;
    try {
      await postMarketingService.deleteScheduled(id);
      showToast('Scheduled post deleted', 'success');
      resetScheduled();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handlePublishScheduled = async (id) => {
    try {
      setLoading(true);
      await postMarketingService.publishScheduled(id);
      showToast('Post published successfully', 'success');
      resetScheduled();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickScheduleDraft = async () => {
    if (!quickScheduleId || !quickScheduleDatetime) return;
    const post = scheduledPosts.find((sp) => sp.id === quickScheduleId);
    if (!post) return;
    try {
      setLoading(true);
      await postMarketingService.updateScheduled(quickScheduleId, {
        ...post,
        scheduled_at: quickScheduleDatetime,
        status: 'scheduled',
        hashtags: Array.isArray(post.hashtags)
          ? post.hashtags
          : (post.hashtags || '')
              .split(',')
              .map((h) => h.trim())
              .filter(Boolean),
      });
      showToast('Draft scheduled successfully', 'success');
      setQuickScheduleId(null);
      setQuickScheduleDatetimeRaw('');
      setQuickScheduleDirty(false);
      resetScheduled();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Tabs ─────────────────────────────────────────────────────────────────

  const tabs = [
    { key: 'synced', label: 'Synced Posts', count: posts.length },
    { key: 'upload', label: 'Upload & Optimise', count: 0 },
    { key: 'scheduled', label: 'Scheduled Posts', count: scheduledPosts.length },
  ];

  // ─── Tab 1: Synced Posts ──────────────────────────────────────────────────

  const renderSyncedTab = () => (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-foreground">KAAY's Synced Posts</h2>
        <button
          onClick={handleSyncPosts}
          disabled={loading}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition text-sm disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Sync Posts
        </button>
      </div>

      {/* Insights Dashboard */}
      {insights && insights.total_analysed > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border rounded-lg p-4 text-center">
            <p className="text-3xl font-bold">{insights.average_score}/10</p>
            <p className="text-sm text-muted-foreground">Avg Score</p>
          </div>
          <div className="bg-card border rounded-lg p-4 text-center">
            <p className="text-3xl font-bold">{insights.total_analysed}</p>
            <p className="text-sm text-muted-foreground">Posts Analysed</p>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <p className="text-sm font-medium text-green-700 dark:text-green-300">Best Post</p>
            <p className="text-xs text-muted-foreground truncate">{insights.best_post?.caption}</p>
            <p className="text-lg font-bold text-green-600 dark:text-green-400">{insights.best_post?.score}/10</p>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Needs Attention</p>
            <p className="text-xs text-muted-foreground truncate">{insights.worst_post?.caption}</p>
            <p className="text-lg font-bold text-red-600 dark:text-red-400">{insights.worst_post?.score}/10</p>
          </div>
        </div>
      )}

      {/* Top improvements banner */}
      {insights && insights.top_improvements && insights.top_improvements.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-950/50 border border-yellow-200 dark:border-yellow-900 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">Top Recurring Improvements:</p>
          <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
            {insights.top_improvements.map((imp, i) => (
              <li key={i}>{imp}</li>
            ))}
          </ul>
        </div>
      )}

      {postsLoading && posts.length === 0 ? (
        <LoadingScreen />
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <BarChart3 size={48} className="mx-auto mb-3 text-muted-foreground/50" />
          <p>No synced posts yet. Click "Sync Posts" to pull KAAY's latest posts.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((post) => (
            <div
              key={post.id}
              onClick={() => handleViewPost(post)}
              className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer relative"
            >
              {post.has_analysis && (
                <div className="absolute top-2 right-2 z-10 bg-green-500 text-white rounded-full p-1 shadow">
                  <Check size={14} />
                </div>
              )}
              {(post.thumbnail_url || post.media_url) && (
                <div className="aspect-square bg-muted overflow-hidden relative">
                  {post.thumbnail_url ? (
                    <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : isVideoPost(post) ? (
                    // No thumbnail on record — fall back to the video's own first frame
                    // rather than feeding an .mp4 URL into <img>, which shows nothing.
                    <video
                      src={post.media_url}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={post.media_url} alt="" className="w-full h-full object-cover" />
                  )}
                  {isVideoPost(post) && (
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white rounded-full p-1">
                      <Play size={12} fill="white" />
                    </div>
                  )}
                </div>
              )}
              <div className="p-4">
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {post.caption
                    ? post.caption.substring(0, 120) + (post.caption.length > 120 ? '...' : '')
                    : 'No caption'}
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full font-medium ${post.has_analysis ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-muted text-muted-foreground'}`}
                  >
                    {post.has_analysis ? 'Analysed' : 'Not Analysed'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground/70">
                  <div className="flex gap-3">
                    <span>{post.like_count ?? 0} likes</span>
                    <span>{post.comment_count ?? 0} comments</span>
                  </div>
                  {post.engagement_rate != null && (
                    <span className="font-medium text-muted-foreground">{post.engagement_rate}% eng.</span>
                  )}
                </div>
                {post.reach != null && (
                  <div className="text-xs text-muted-foreground/70 mt-1">Reach: {post.reach?.toLocaleString()}</div>
                )}
                {post.posted_at && (
                  <div className="text-xs text-muted-foreground/70 mt-1">
                    {new Date(post.posted_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={postsSentinelRef} />
      {postsLoading && posts.length > 0 && (
        <div className="flex justify-center py-4">
          <LoadingSpinner size="sm" />
        </div>
      )}
    </div>
  );

  // ─── Post Detail Drawer ───────────────────────────────────────────────────

  const renderPostDetailDrawer = () => {
    if (!selectedPost) return null;
    const post = selectedPost;

    return (
      <ModalDrawer
        maxWidth="lg"
        onClose={() => {
          setSelectedPost(null);
          setSelectedAnalysis(null);
        }}
      >
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold">Post Details</h3>
          <button
            onClick={() => {
              setSelectedPost(null);
              setSelectedAnalysis(null);
            }}
            className="p-1 hover:bg-accent rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Media */}
          {(post.thumbnail_url || post.media_url) && (
            <div className="rounded-lg overflow-hidden bg-muted">
              {isVideoPost(post) && post.media_url ? (
                <video
                  src={post.media_url}
                  poster={post.thumbnail_url || undefined}
                  controls
                  playsInline
                  className="w-full"
                />
              ) : isVideoPost(post) ? (
                // Thumbnail exists but the video file itself was never synced from
                // Instagram (media_url is null on this row) — show the still, not a
                // player with nothing to play. Re-syncing this specific post won't
                // fill it in; the sync only overwrites an already-set media_url.
                <div className="relative">
                  <img src={post.thumbnail_url} alt="" className="w-full object-cover" />
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                    Video file unavailable — only the cover image was synced
                  </div>
                </div>
              ) : (
                <img src={post.media_url} alt="" className="w-full object-cover" />
              )}
            </div>
          )}

          {/* Caption */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">Caption</h4>
            <p className="text-sm text-foreground whitespace-pre-wrap">{post.caption || 'No caption'}</p>
          </div>

          {/* Stats Panel */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3">Performance</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'Likes', value: post.like_count ?? 0 },
                { label: 'Comments', value: post.comment_count ?? 0 },
                { label: 'Shares', value: post.share_count ?? 0 },
                { label: 'Reach', value: post.reach ?? 0 },
                { label: 'Impressions', value: post.impressions ?? 0 },
                { label: 'Saved', value: post.saved_count ?? 0 },
                {
                  label: 'Engagement Rate',
                  value: post.engagement_rate != null ? `${post.engagement_rate}%` : 'N/A',
                },
              ].map((stat) => (
                <div key={stat.label} className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-lg font-semibold text-foreground">
                    {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* AI Analysis Section */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Sparkles size={16} className="text-purple-500" />
                AI Analysis
              </h4>
              {selectedAnalysis && (
                <button
                  onClick={() => handleAnalysePost(post.id)}
                  disabled={analysingPostId === post.id}
                  className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 disabled:opacity-50"
                >
                  <RefreshCw size={12} className={analysingPostId === post.id ? 'animate-spin' : ''} />
                  Re-analyse
                </button>
              )}
            </div>

            {analysingPostId === post.id ? (
              <div className="flex items-center justify-center py-8">
                <Loader size={20} className="animate-spin text-purple-400" />
                <span className="ml-2 text-sm text-muted-foreground">Analysing post...</span>
              </div>
            ) : selectedAnalysis ? (
              <div className="space-y-4">
                {/* Score Badge */}
                {selectedAnalysis.improvement_suggestions?.score != null && (
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold ${
                        selectedAnalysis.improvement_suggestions.score >= 7
                          ? 'bg-green-500'
                          : selectedAnalysis.improvement_suggestions.score >= 4
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      }`}
                    >
                      {selectedAnalysis.improvement_suggestions.score}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Overall Score</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedAnalysis.improvement_suggestions.score >= 7
                          ? 'Great performance'
                          : selectedAnalysis.improvement_suggestions.score >= 4
                            ? 'Room for improvement'
                            : 'Needs attention'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Caption Analysis */}
                {selectedAnalysis.improvement_suggestions?.caption_analysis && (
                  <div className="bg-purple-50 dark:bg-purple-950/50 border border-purple-200 dark:border-purple-900 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-2">Caption Analysis</h5>
                    <p className="text-sm text-purple-700 dark:text-purple-300 whitespace-pre-wrap">
                      {selectedAnalysis.improvement_suggestions.caption_analysis}
                    </p>
                  </div>
                )}

                {/* Timing Analysis */}
                {selectedAnalysis.improvement_suggestions?.timing_analysis && (
                  <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Timing Analysis</h5>
                    <p className="text-sm text-blue-700 dark:text-blue-300 whitespace-pre-wrap">
                      {selectedAnalysis.improvement_suggestions.timing_analysis}
                    </p>
                  </div>
                )}

                {/* Content Type */}
                {selectedAnalysis.improvement_suggestions?.content_type_analysis && (
                  <div className="bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-900 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-2">Content Type</h5>
                    <p className="text-sm text-indigo-700 dark:text-indigo-300 whitespace-pre-wrap">
                      {selectedAnalysis.improvement_suggestions.content_type_analysis}
                    </p>
                  </div>
                )}

                {/* vs Competitors */}
                {selectedAnalysis.improvement_suggestions?.competitor_comparison && (
                  <div className="bg-orange-50 dark:bg-orange-950/50 border border-orange-200 dark:border-orange-900 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-orange-800 dark:text-orange-300 mb-2">vs Competitors</h5>
                    <p className="text-sm text-orange-700 dark:text-orange-300 whitespace-pre-wrap">
                      {selectedAnalysis.improvement_suggestions.competitor_comparison}
                    </p>
                  </div>
                )}

                {/* Improvements Checklist */}
                {selectedAnalysis.improvement_suggestions?.improvements &&
                  Array.isArray(selectedAnalysis.improvement_suggestions.improvements) && (
                    <div className="bg-yellow-50 dark:bg-yellow-950/50 border border-yellow-200 dark:border-yellow-900 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">Improvements</h5>
                      <ul className="space-y-2">
                        {selectedAnalysis.improvement_suggestions.improvements.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-yellow-700 dark:text-yellow-300">
                            <span className="inline-block w-5 h-5 border border-yellow-400 rounded-md mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Recommended Caption */}
                {selectedAnalysis.improvement_suggestions?.recommended_caption && (
                  <div className="bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-900 rounded-lg p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                      <h5 className="text-sm font-medium text-green-800 dark:text-green-300">Recommended Caption</h5>
                      <button
                        onClick={() =>
                          handleCopy(selectedAnalysis.improvement_suggestions.recommended_caption, 'analysis-caption')
                        }
                        className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                      >
                        {copiedField === 'analysis-caption' ? <Check size={14} /> : <Copy size={14} />}
                        {copiedField === 'analysis-caption' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300 whitespace-pre-wrap">
                      {selectedAnalysis.improvement_suggestions.recommended_caption}
                    </p>
                  </div>
                )}

                {/* Fallback: show old-style fields if improvement_suggestions is not the new JSON format */}
                {!selectedAnalysis.improvement_suggestions?.score && (
                  <>
                    {selectedAnalysis.worked_well && (
                      <div className="bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-900 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                          What Worked Well
                        </h5>
                        <p className="text-sm text-green-700 dark:text-green-300 whitespace-pre-wrap">
                          {selectedAnalysis.worked_well}
                        </p>
                      </div>
                    )}
                    {selectedAnalysis.underperformed && (
                      <div className="bg-yellow-50 dark:bg-yellow-950/50 border border-yellow-200 dark:border-yellow-900 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                          Underperformed
                        </h5>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 whitespace-pre-wrap">
                          {selectedAnalysis.underperformed}
                        </p>
                      </div>
                    )}
                    {selectedAnalysis.improvements && (
                      <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Improvements</h5>
                        {Array.isArray(selectedAnalysis.improvements) ? (
                          <ul className="list-disc list-inside space-y-1">
                            {selectedAnalysis.improvements.map((item, idx) => (
                              <li key={idx} className="text-sm text-blue-700 dark:text-blue-300">
                                {item}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-blue-700 dark:text-blue-300 whitespace-pre-wrap">
                            {selectedAnalysis.improvements}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-8 bg-muted/50 rounded-lg">
                <Sparkles size={32} className="mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground mb-3">No analysis available for this post.</p>
                <button
                  onClick={() => handleAnalysePost(post.id)}
                  disabled={analysingPostId === post.id}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  Analyse Post
                </button>
              </div>
            )}
          </div>

          {/* Linked Product Section */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <ShoppingBag size={16} className="text-indigo-500" />
              Linked Product
            </h4>

            {/* Current linked product display */}
            {linkedProductId &&
              (() => {
                const product = allProducts.find((p) => p.id === linkedProductId);
                return (
                  <div className="flex items-center gap-3 mb-3 bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-900 rounded-lg px-3 py-2">
                    {product?.image_urls?.[0] && (
                      <img
                        src={product.image_urls[0]}
                        alt=""
                        className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                      />
                    )}
                    <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300 flex-1 truncate">
                      {product?.name || `Product #${linkedProductId}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleLinkProduct(null)}
                      disabled={savingLink}
                      className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50"
                    >
                      Unlink
                    </button>
                  </div>
                );
              })()}

            {/* Product dropdown */}
            <select
              value={linkedProductId || ''}
              onChange={(e) => handleLinkProduct(e.target.value || null)}
              disabled={savingLink}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent disabled:opacity-50"
            >
              <option value="">No product linked</option>
              {allProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>

            {savingLink && (
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader size={12} className="animate-spin" />
                Updating...
              </div>
            )}
          </div>

          {/* Posted Date */}
          {post.posted_at && (
            <div className="text-xs text-muted-foreground/70 pt-4 border-t border-border/60">
              Posted: {new Date(post.posted_at).toLocaleString()}
            </div>
          )}
        </div>
      </ModalDrawer>
    );
  };

  // ─── Tab 2: Upload & Optimise ─────────────────────────────────────────────

  const renderUploadTab = () => (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-lg font-semibold text-foreground mb-4">Upload & Optimise</h2>

      {/* File Upload */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground/80 mb-2">Media</label>
        <FileUpload
          uploadType="post-media"
          accept="image/*,video/*"
          multiple={false}
          maxFiles={1}
          onUploadComplete={(files) => {
            if (files.length > 0) {
              setUploadedMediaUrl(files[0].url);
            }
          }}
        />
        {uploadedMediaUrl && (
          <div className="mt-2 flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
            <Check size={14} />
            Media uploaded
          </div>
        )}
      </div>

      {/* 2-column grid for Target Market, Post Type, Location Tag */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Target Market — searchable dropdown */}
        <SearchableDropdown
          label="Target Market"
          value={targetMarket}
          onChange={setTargetMarket}
          options={TARGET_MARKET_OPTIONS}
          placeholder="Select target market"
        />

        {/* Post Type dropdown */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Post Type</label>
          <select
            value={postType}
            onChange={(e) => setPostType(e.target.value)}
            className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent bg-card"
          >
            <option value="">Select post type</option>
            {POST_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {/* Location Tag */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Location Tag</label>
          <input
            type="text"
            value={locationTag}
            onChange={(e) => setLocationTag(e.target.value)}
            placeholder="e.g., Mumbai, India"
            className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
          />
        </div>
      </div>

      {/* Caption Draft */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground/80 mb-1">Caption Draft</label>
        <textarea
          value={captionDraft}
          onChange={(e) => setCaptionDraft(e.target.value)}
          rows={4}
          placeholder="Write your caption draft here..."
          className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
        />
      </div>

      {/* Get Recommendation Button */}
      <button
        onClick={handleGetRecommendation}
        disabled={recommendLoading}
        className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-lg hover:bg-primary/90 transition text-sm disabled:opacity-50 mb-8"
      >
        {recommendLoading ? <Loader size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {recommendLoading ? 'Generating...' : 'Get AI Recommendation'}
      </button>

      {/* Recommendation Result */}
      {recommendation && (
        <div className="space-y-4 border-t border-border pt-6">
          <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Sparkles size={18} className="text-purple-500" />
            AI Recommendation
          </h3>

          {/* Recommended Caption */}
          {recommendation.recommended_caption && (
            <div className="bg-purple-50 dark:bg-purple-950/50 border border-purple-200 dark:border-purple-900 rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300">Recommended Caption</h4>
                <button
                  onClick={() => handleCopy(recommendation.recommended_caption, 'caption')}
                  className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                >
                  {copiedField === 'caption' ? <Check size={14} /> : <Copy size={14} />}
                  {copiedField === 'caption' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="text-sm text-purple-900 dark:text-purple-300 whitespace-pre-wrap">
                {recommendation.recommended_caption}
              </p>
            </div>
          )}

          {/* Best Posting Time */}
          {recommendation.best_posting_time && (
            <div className="flex items-center gap-3 bg-card border border-border rounded-lg p-4">
              <Clock size={20} className="text-blue-500 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-foreground/80">Best Posting Time</h4>
                <p className="text-sm text-foreground">{recommendation.best_posting_time}</p>
              </div>
            </div>
          )}

          {/* Location Tag */}
          {recommendation.location_tag && (
            <div className="flex items-center gap-3 bg-card border border-border rounded-lg p-4">
              <MapPin size={20} className="text-red-500 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-foreground/80">Location Tag Suggestion</h4>
                <p className="text-sm text-foreground">{recommendation.location_tag}</p>
              </div>
            </div>
          )}

          {/* Hashtags */}
          {recommendation.hashtags && (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Hash size={18} className="text-indigo-500" />
                <h4 className="text-sm font-medium text-foreground/80">Hashtag Suggestions</h4>
                <button
                  onClick={() =>
                    handleCopy(
                      Array.isArray(recommendation.hashtags)
                        ? recommendation.hashtags.join(' ')
                        : recommendation.hashtags,
                      'hashtags',
                    )
                  }
                  className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  {copiedField === 'hashtags' ? <Check size={14} /> : <Copy size={14} />}
                  {copiedField === 'hashtags' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(Array.isArray(recommendation.hashtags)
                  ? recommendation.hashtags
                  : recommendation.hashtags.split(',').map((h) => h.trim())
                ).map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 text-xs rounded-full"
                  >
                    {tag.startsWith('#') ? tag : `#${tag}`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Audience Tip */}
          {recommendation.audience_tip && (
            <div className="flex items-start gap-3 bg-yellow-50 dark:bg-yellow-950/50 border border-yellow-200 dark:border-yellow-900 rounded-lg p-4">
              <Eye size={20} className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Audience Tip</h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">{recommendation.audience_tip}</p>
              </div>
            </div>
          )}

          {/* Schedule This Post Button */}
          <button
            onClick={handleOpenScheduleFromRecommendation}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition text-sm"
          >
            <Calendar size={16} />
            Schedule This Post
          </button>
        </div>
      )}
    </div>
  );

  // ─── Tab 3: Scheduled Posts ───────────────────────────────────────────────

  const renderScheduledTab = () => (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-foreground">Scheduled Posts</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setCreateForm({
                caption: '',
                scheduled_at: '',
                location_tag: '',
                hashtags: '',
                status: 'draft',
                media_url: '',
              });
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition text-sm"
          >
            <Plus size={16} />
            Create New Post
          </button>
          <button
            onClick={resetScheduled}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 text-sm disabled:opacity-50"
          >
            <RefreshCw size={16} className={scheduledLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {scheduledLoading && scheduledPosts.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader size={24} className="animate-spin text-muted-foreground/70" />
        </div>
      ) : scheduledPosts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar size={48} className="mx-auto mb-3 text-muted-foreground/50" />
          <p>No scheduled or draft posts yet. Click "Create New Post" to add one.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Media</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Caption</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Scheduled At</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Created At</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {scheduledPosts.map((sp) => (
                <tr key={sp.id} className="hover:bg-muted/50">
                  <td className="px-4 py-3">
                    {sp.media_url ? (
                      looksLikeVideoUrl(sp.media_url) ? (
                        <video
                          src={sp.media_url}
                          muted
                          playsInline
                          preload="metadata"
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                      ) : (
                        <img src={sp.media_url} alt="" className="w-12 h-12 object-cover rounded-lg" />
                      )
                    ) : (
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                        <Image size={16} className="text-muted-foreground/50" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-foreground max-w-xs">
                    <p className="truncate">
                      {sp.caption ? sp.caption.substring(0, 80) + (sp.caption.length > 80 ? '...' : '') : '—'}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge value={sp.status} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {sp.scheduled_at ? new Date(sp.scheduled_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {sp.created_at ? new Date(sp.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Schedule button for draft rows */}
                      {sp.status === 'draft' && (
                        <button
                          onClick={() => {
                            setQuickScheduleId(sp.id);
                            setQuickScheduleDatetimeRaw('');
                            setQuickScheduleDirty(false);
                          }}
                          className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/70 rounded-lg"
                          title="Schedule"
                        >
                          <Calendar size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingScheduledRaw({
                            ...sp,
                            hashtags: Array.isArray(sp.hashtags) ? sp.hashtags.join(', ') : sp.hashtags || '',
                            scheduled_at: sp.scheduled_at ? sp.scheduled_at.substring(0, 16) : '',
                          });
                          setEditingScheduledDirty(false);
                        }}
                        className="p-1.5 text-muted-foreground hover:bg-accent rounded-lg"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteScheduled(sp.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/70 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                      {(sp.status === 'draft' || sp.status === 'scheduled') && (
                        <button
                          onClick={() => handlePublishScheduled(sp.id)}
                          disabled={loading}
                          className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/70 rounded-lg disabled:opacity-50"
                          title="Publish Now"
                        >
                          <Play size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={scheduledSentinelRef} />
      {scheduledLoading && scheduledPosts.length > 0 && (
        <div className="flex justify-center py-4">
          <LoadingSpinner size="sm" />
        </div>
      )}
    </div>
  );

  // ─── Schedule Modal (from recommendation flow) ────────────────────────────

  const renderScheduleModal = () => {
    if (!showScheduleModal) return null;

    return (
      <Modal
        maxWidth="lg"
        onClose={closeScheduleModal}
        className="rounded-lg p-6"
        closeOnBackdrop={false}
        hasUnsavedChanges={scheduleDirty}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Schedule Post</h3>
          <button onClick={closeScheduleModal} className="p-1 hover:bg-accent rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Media preview */}
          {scheduleForm.media_url && (
            <div className="rounded-lg overflow-hidden bg-muted">
              {looksLikeVideoUrl(scheduleForm.media_url) ? (
                <video src={scheduleForm.media_url} controls playsInline className="w-full max-h-48" />
              ) : (
                <img src={scheduleForm.media_url} alt="" className="w-full max-h-48 object-cover" />
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Caption</label>
            <textarea
              value={scheduleForm.caption}
              onChange={(e) => setScheduleForm({ ...scheduleForm, caption: e.target.value })}
              rows={4}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Scheduled Date & Time</label>
            <input
              type="datetime-local"
              value={scheduleForm.scheduled_at}
              onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_at: e.target.value })}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Location Tag</label>
            <input
              type="text"
              value={scheduleForm.location_tag}
              onChange={(e) => setScheduleForm({ ...scheduleForm, location_tag: e.target.value })}
              placeholder="e.g., Mumbai, India"
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Hashtags (comma-separated)</label>
            <input
              type="text"
              value={scheduleForm.hashtags}
              onChange={(e) => setScheduleForm({ ...scheduleForm, hashtags: e.target.value })}
              placeholder="#luxury, #fashion, #kaay"
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                handleCreateScheduled({ ...scheduleForm, status: 'draft' }, true);
              }}
              disabled={loading}
              className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
            >
              Save as Draft
            </button>
            <button
              type="button"
              onClick={() => {
                if (!scheduleForm.scheduled_at) {
                  showToast('Please pick a date and time to schedule', 'error');
                  return;
                }
                handleCreateScheduled({ ...scheduleForm, status: 'scheduled' }, true);
              }}
              disabled={loading}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // ─── Create New Post Modal (from Scheduled tab) ───────────────────────────

  const renderCreateModal = () => {
    if (!showCreateModal) return null;

    return (
      <Modal
        maxWidth="lg"
        onClose={closeCreateModal}
        className="rounded-lg p-6"
        closeOnBackdrop={false}
        hasUnsavedChanges={createDirty}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Create New Post</h3>
          <button onClick={closeCreateModal} className="p-1 hover:bg-accent rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Media Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Media</label>
            <FileUpload
              uploadType="post-media"
              accept="image/*,video/*"
              multiple={false}
              maxFiles={1}
              onUploadComplete={(files) => {
                if (files.length > 0) {
                  setCreateForm({ ...createForm, media_url: files[0].url });
                }
              }}
            />
            {createForm.media_url && (
              <div className="mt-2 flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                <Check size={14} />
                Media uploaded
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Caption</label>
            <textarea
              value={createForm.caption}
              onChange={(e) => setCreateForm({ ...createForm, caption: e.target.value })}
              rows={4}
              placeholder="Write your caption..."
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Location Tag</label>
            <input
              type="text"
              value={createForm.location_tag}
              onChange={(e) => setCreateForm({ ...createForm, location_tag: e.target.value })}
              placeholder="e.g., Mumbai, India"
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Hashtags (comma-separated)</label>
            <input
              type="text"
              value={createForm.hashtags}
              onChange={(e) => setCreateForm({ ...createForm, hashtags: e.target.value })}
              placeholder="#luxury, #fashion, #kaay"
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Schedule Date & Time</label>
            <input
              type="datetime-local"
              value={createForm.scheduled_at}
              onChange={(e) => setCreateForm({ ...createForm, scheduled_at: e.target.value })}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
            <p className="text-xs text-muted-foreground/70 mt-1">Leave empty to save as a draft</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Status</label>
            <select
              value={createForm.status}
              onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent bg-card"
            >
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeCreateModal}
              className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                const status = createForm.scheduled_at ? createForm.status : 'draft';
                if (status === 'scheduled' && !createForm.scheduled_at) {
                  showToast('Please pick a date and time to schedule', 'error');
                  return;
                }
                handleCreateScheduled({ ...createForm, status }, false);
              }}
              disabled={loading}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Create Post'}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // ─── Quick Schedule Draft Modal ───────────────────────────────────────────

  const renderQuickScheduleModal = () => {
    if (!quickScheduleId) return null;

    return (
      <Modal
        maxWidth="sm"
        fullHeight={false}
        closeOnBackdrop={false}
        className="rounded-lg p-6"
        onClose={closeQuickScheduleModal}
        hasUnsavedChanges={quickScheduleDirty}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Schedule Draft</h3>
          <button onClick={closeQuickScheduleModal} className="p-1 hover:bg-accent rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Pick Date & Time</label>
            <input
              type="datetime-local"
              value={quickScheduleDatetime}
              onChange={(e) => setQuickScheduleDatetime(e.target.value)}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeQuickScheduleModal}
              className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (!quickScheduleDatetime) {
                  showToast('Please pick a date and time', 'error');
                  return;
                }
                handleQuickScheduleDraft();
              }}
              disabled={loading}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // ─── Edit Scheduled Modal ─────────────────────────────────────────────────

  const renderEditScheduledModal = () => {
    if (!editingScheduled) return null;

    return (
      <Modal
        maxWidth="lg"
        onClose={closeEditScheduledModal}
        className="rounded-lg p-6"
        closeOnBackdrop={false}
        hasUnsavedChanges={editingScheduledDirty}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Edit Scheduled Post</h3>
          <button onClick={closeEditScheduledModal} className="p-1 hover:bg-accent rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Caption</label>
            <textarea
              value={editingScheduled.caption || ''}
              onChange={(e) => setEditingScheduled({ ...editingScheduled, caption: e.target.value })}
              rows={4}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Scheduled Date & Time</label>
            <input
              type="datetime-local"
              value={editingScheduled.scheduled_at || ''}
              onChange={(e) => setEditingScheduled({ ...editingScheduled, scheduled_at: e.target.value })}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Location Tag</label>
            <input
              type="text"
              value={editingScheduled.location_tag || ''}
              onChange={(e) => setEditingScheduled({ ...editingScheduled, location_tag: e.target.value })}
              placeholder="e.g., Mumbai, India"
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Hashtags (comma-separated)</label>
            <input
              type="text"
              value={editingScheduled.hashtags || ''}
              onChange={(e) => setEditingScheduled({ ...editingScheduled, hashtags: e.target.value })}
              placeholder="#luxury, #fashion, #kaay"
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Status</label>
            <select
              value={editingScheduled.status || 'draft'}
              onChange={(e) => setEditingScheduled({ ...editingScheduled, status: e.target.value })}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeEditScheduledModal}
              className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpdateScheduled}
              disabled={loading}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Post Management</h1>
          <p className="text-muted-foreground mt-1">Manage, analyse, and schedule KAAY's Instagram posts</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 whitespace-nowrap flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition ${
              activeTab === tab.key
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span
                className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.key ? 'bg-primary text-primary-foreground' : 'bg-accent text-muted-foreground'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'synced' && renderSyncedTab()}
      {activeTab === 'upload' && renderUploadTab()}
      {activeTab === 'scheduled' && renderScheduledTab()}

      {/* Modals / Drawers */}
      {renderPostDetailDrawer()}
      {renderScheduleModal()}
      {renderCreateModal()}
      {renderQuickScheduleModal()}
      {renderEditScheduledModal()}
    </div>
  );
};

export default PostManagementPage;
